import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { checkRevertFundPayment, revertFundPayment } from 'lib/api/funds';
import { getResellPayments } from 'lib/api/reSell/factoring';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useFactoringResell } from '../../../../context';

const headers: { key: string; label: string; type?: string }[] = [
  { key: 'date', label: 'Fecha', type: 'date' },
  { key: 'amount', label: 'Monto', type: 'money' },
  {
    key: 'conciliationDocumentAmount',
    label: 'Conciliado documento cliente',
    type: 'money',
  },
  {
    key: 'conciliationArrearAmount',
    label: 'Conciliado mora cliente',
    type: 'money',
  },
  {
    key: 'arrearRate',
    label: 'Tasa mora aplicada cliente',
    type: 'percentage',
  },
];

interface PaymentItem {
  id: number;
  amount: number;
  date: string;
}

const ResellPayments: React.FC = () => {
  const { resell } = useFactoringResell();
  if (!resell) return null;
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState<PaymentItem[]>([]);
  const [warningMessages, setWarningMessages] = useState<string[]>([]);
  const [canRevert, setCanRevert] = useState(false);
  const [reason, setReason] = useState('');
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | number>(
    '',
  );

  const resellId = resell.id;

  const fetchFundPayments = async () => {
    if (!resellId) return;
    setLoading(true);
    try {
      const response = await getResellPayments(dispatch, resellId);
      setPayments(response.fundPayments);
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al cargar los pagos');
    }
    setLoading(false);
  };

  const handleReversePreview = async (paymentId: string | number) => {
    setLoading(true);
    try {
      const check = await checkRevertFundPayment(dispatch, { id: paymentId });
      setWarningMessages(check.warningMessages);
      setCanRevert(check.canRevert);
      setReason(check.reason);
      setOpenPreviewDialog(true);
      setSelectedPaymentId(paymentId);
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al obtener el preview de reversa');
    }
    setLoading(false);
  };

  const handleConfirmReverse = async () => {
    setOpenPreviewDialog(false);
    setLoading(true);
    try {
      await revertFundPayment(dispatch, { id: selectedPaymentId });
      showAlert(dispatch, 'success', 'Pago reversado correctamente');
      await fetchFundPayments();
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al reversar el pago');
    }
    setLoading(false);
  };

  const actions = [
    {
      actionLabel: 'Reversar',
      action: (paymentId: string | number) => handleReversePreview(paymentId),
    },
  ];

  useEffect(() => {
    void fetchFundPayments();
  }, []);

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <div>
      <HeaderWithActions
        title="Acá encontrarás todos los avisos de pagos registrados para esta venta a fondo"
        variant="body1"
        buttons={[]}
      />
      <EnhancedTable
        dataHeaders={headers}
        data={payments}
        rowsPerPageDefault={50}
        actions={actions}
        showActions
      />
      <Dialog maxWidth="md" open={openPreviewDialog}>
        <DialogTitle>Reversar pago</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            ¿Estás seguro que quieres reversar este pago? Se ejecutarán las
            siguientes acciones:
          </Typography>
          <div style={{ padding: '10px 0px' }}>
            {warningMessages?.map((message: string, idx: number) => (
              <Typography key={idx} variant="body1" color="error">
                - {message}
              </Typography>
            ))}
          </div>
          {!canRevert && reason && (
            <div style={{ padding: '10px 0px', marginTop: '10px' }}>
              <Typography variant="body1" color="error">
                {reason}
              </Typography>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <CustomButton
            color="secondary"
            onClick={() => setOpenPreviewDialog(false)}
            disabled={loading}
          >
            Salir
          </CustomButton>
          <CustomButton
            onClick={handleConfirmReverse}
            color="error"
            disabled={!canRevert || loading}
          >
            Reversar
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ResellPayments;
